/**
 * TutorialOverlay — three-page how-to-play modal. Shows on first launch
 * (via App's state) and re-openable from Settings.
 */
import { useState } from 'react';

interface Props {
  readonly onClose: () => void;
}

interface Page {
  readonly title: string;
  /** HTML — we control the source so it's safe to dangerouslySetInnerHTML. */
  readonly body: string;
}

const PAGES: readonly Page[] = [
  {
    title: 'The mechanic',
    body: `<p>Start with one word at the top. Reach the target word at the bottom.</p>
           <p>Each step changes <strong>exactly one letter</strong>. Every intermediate must be a real word.</p>
           <div class="demo">HEAD → HEAL → TEAL → TELL → TALL → TAIL</div>`,
  },
  {
    title: 'How you play',
    body: `<p>Tap the highlighted slot and type a word. If it's valid and one letter from the line above, it fills.</p>
           <p>A definition of every word you complete appears underneath.</p>
           <p>The puzzle accepts <strong>any</strong> valid chain of the right length — not just the canonical one.</p>`,
  },
  {
    title: `When you're stuck`,
    body: `<p>Tap <strong>Hint</strong> at the bottom. The hint escalates each time you tap:</p>
           <p><em>1st tap</em> — the meaning of the next word.</p>
           <p><em>2nd tap</em> — which letter position changes.</p>
           <p><em>3rd tap</em> — what the letter is.</p>
           <p><em>4th tap</em> — fills the word.</p>
           <p>No timer, no streak, no score. Take your time.</p>`,
  },
];

export function TutorialOverlay(props: Props) {
  const { onClose } = props;
  const [idx, setIdx] = useState(0);
  const isLast = idx === PAGES.length - 1;
  const page = PAGES[idx]!;

  const advance = () => {
    if (isLast) onClose();
    else setIdx(idx + 1);
  };

  return (
    <div id="tutorialOverlay" className="overlay">
      <div className="overlayScrim" onClick={onClose}></div>
      <div className="overlayCard" role="dialog" aria-label="How to play">
        <header>
          <h2>How to play</h2>
          <button className="iconBtn ghost" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="tutBody">
          <h3>{page.title}</h3>
          <div dangerouslySetInnerHTML={{ __html: page.body }} />
        </div>
        <div className="tutDots">
          {PAGES.map((_, i) => (
            <span key={i} className={'dot' + (i === idx ? ' active' : '')}></span>
          ))}
        </div>
        <button onClick={advance}>{isLast ? 'Got it' : 'Next'}</button>
      </div>
    </div>
  );
}
